import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../services/api";

interface ProductDetailsFlowProps {
  isOpen: boolean;
  onClose: (selectedDetails?: ProductDetails) => void;
  projectId: string;
  productPrompt?: string;
}

interface ProductDetails {
  gender: string;
  ethnicity: string;
  dimensions: string;
}

export default function ProductDetailsFlow({
  isOpen,
  onClose,
  projectId,
  productPrompt,
}: ProductDetailsFlowProps) {
  const [step, setStep] = useState(1);
  const [productDetails, setProductDetails] = useState<ProductDetails>({
    gender: "",
    ethnicity: "",
    dimensions: "",
  });
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Using the same emojis and labels as in AIActorSelectionDialog
  // Important: values must match CATEGORY_MAP values in AIActorSelectionDialog
  const genderOptions = [
    { value: "Female", label: "Woman", emoji: "👩" }, // Capitalized to match backend
    { value: "Male", label: "Man", emoji: "👨" },     // Capitalized to match backend
    { value: "any", label: "Any", emoji: "🧑" },
  ];

  const ethnicityOptions = [
    { value: "Caucasian", label: "Caucasian", emoji: "👩‍🦰" },
    { value: "African American", label: "Black/African", emoji: "👩🏿" },
    { value: "Southeast Asian", label: "Southeast Asian", emoji: "👩🏼" },
    { value: "Hispanic or Latino", label: "Hispanic", emoji: "👩🏽" },
    { value: "East Asian", label: "East Asian", emoji: "👩🏻" },
    { value: "South Asian", label: "South Asian", emoji: "👳🏾" },
    { value: "Middle Eastern or North African", label: "Middle Eastern", emoji: "👳" },
    { value: "any", label: "Any", emoji: "🌍" },
  ];

  // Load existing data from project metadata
  useEffect(() => {
    if (isOpen && projectId) {
      loadProductDetails();
    }
  }, [isOpen, projectId]);

  const loadProductDetails = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/projects/project-metadata/${projectId}`);
      const metadata = response.data.metadata || {};
      
      if (metadata.productDetails) {
        setProductDetails(metadata.productDetails);
        setIsComplete(true);
        onClose(metadata.productDetails); // If details already exist, pass them when closing
      }
    } catch (error) {
      console.error("Error loading product details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProductDetails = async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      await api.post(`/api/projects/project-metadata/${projectId}`, {
        metadata: { productDetails },
      });
      setIsComplete(true);
      onClose(productDetails);
    } catch (error) {
      console.error("Error saving product details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      saveProductDetails();
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      }
    },
    [step]
  );

  const handleGenderSelect = (gender: string) => {
    setProductDetails({ ...productDetails, gender });
    // Automatically go to next step after selection
    setTimeout(() => handleNext(), 300);
  };

  const handleEthnicitySelect = (ethnicity: string) => {
    setProductDetails({ ...productDetails, ethnicity });
    // Automatically go to next step after selection
    setTimeout(() => handleNext(), 300);
  };

  const handleDimensionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductDetails({ ...productDetails, dimensions: e.target.value });
  };

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, x: -300, transition: { duration: 0.3, ease: "easeIn" } },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, delay: 0.1 } },
    exit: { opacity: 0, x: -100, transition: { duration: 0.3 } },
  };

  // Only show dialog when it's open, not complete, and not loading
  if (!isOpen || isComplete || isLoading) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <motion.div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Product Details
            </h2>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Step {step} of 3
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mb-6">
            <div 
              className="h-full bg-blue-500 dark:bg-blue-600 rounded-full transition-all duration-500"
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="gender-step"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="mb-4"
              >
                <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
                  What gender do you want? 
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {genderOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleGenderSelect(option.value)}
                      className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:cursor-pointer
                        ${productDetails.gender === option.value
                          ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-700"}`}
                    >
                      <span className="text-3xl mb-2">{option.emoji}</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="ethnicity-step"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="mb-4"
              >
                <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
                  What ethnicity do you prefer?
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {ethnicityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleEthnicitySelect(option.value)}
                      className={`flex items-center p-3 border-2 rounded-lg transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:cursor-pointer
                        ${productDetails.ethnicity === option.value
                          ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-700"}`}
                    >
                      <span className="text-2xl mr-2">{option.emoji}</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="dimensions-step"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="mb-4"
              >
                <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-gray-200">
                  What are your product dimensions?
                </h3>
                <div className="mb-4">
                  <input
                    type="text"
                    value={productDetails.dimensions}
                    onChange={handleDimensionsChange}
                    onKeyDown={handleKeyPress}
                    placeholder="e.g. 3 x 3 x 5 inches (we support any units)"
                    className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200"
                    autoFocus
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Enter dimensions in any format or units that describe your product best.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors duration-200"
                disabled={isLoading}
              >
                Back
              </button>
            ) : (
              <div></div> // Empty div to maintain spacing
            )}
            <button
              onClick={handleNext}
              disabled={
                (step === 1 && !productDetails.gender) ||
                (step === 2 && !productDetails.ethnicity) ||
                isLoading
              }
              className="px-6 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : step === 3 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </button>
          </div>
          
          <div className="text-center mt-4 text-sm text-gray-500 dark:text-gray-400">
            Press Enter ↵ to continue
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}